/* eslint-disable max-lines-per-function */
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import * as ReactDOMServer from 'react-dom/server'
import {Lang} from 'yougile-lang'
import {logger} from 'yougile-util'
import {DataObjectId} from 'yougile-model'
import {AvatarBase} from '@components/common/base/avatar'
import {MarkUnreadButton} from '@components/common/base/chat-message/mark-unread-button'
import {UserNameBase} from '@components/common/base/user-name'
import {EmojiPicker} from '@components/common/emoji-picker/emoji-picker'
import {useChatStore} from '@components/right-panel/chat/chat-store'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {AdditionalInfo} from './additional-info'
import {CitedMessage} from './cited-message'
import {
  TwButtons,
  TwButtonsRow,
  TwChatMessage,
  TwChatMessageBottom,
  TwChatMessageContainer,
  TwTail,
  TwUiCnt
} from './containers'
import {DeleteButton} from './delete-button'
import {EditButton} from './edit-button'
import {FileList} from './file-list'
import {ForwardButton} from './forward-button'
import {ImageMenuDropdown, ImageMenuIcon} from './image-menu'
import {MarkImportantButton} from './mark-important-button'
import {Message} from './message'
import {PinButton} from './pin-button'
import {PinnedLabel, PinnedLabelEditing} from './pin-label'
import {ReactionsList} from './reactions-list'
import {ReplyButton} from './reply-button'
import {ChatMessageBaseProps, ChatMessageReaction, SeenBy} from './types'
import {VotingComp} from './voting/voting-comp'

export const ChatMessageBase = ({
  messageData: {
    user,
    userName,
    userNameType,
    html,
    timestamp,
    editedTimestamp,
    emojiOnly,
    my,
    label,
    files
  },
  forwardedMessageData,
  replyMessageData,
  fullView = true,
  hideAvatar = false,
  seenBy,
  seenByTitle,
  labelEditing,
  onLabelSubmit,
  className,
  onReply,
  onForward,
  onEditPin,
  onTogglePin,
  onEdit,
  onDelete,
  onMarkUnread,
  onMarkImportant,
  onMessageClick,
  onRepliedMessageClick,
  onForwardedMessageClick,
  onReactionSelect,
  currentUserId,
  reactions,
  animateShow,
  avatarRef,
  onAvatarClick,
  editing,
  onFileClick,
  onSetBackground,
  onRemoveBackground,
  onDownloadFile,
  lastImage,
  canMarkUnread,
  isMarkUnreadActive,
  voting,
  msgTs,
  hubId
}: ChatMessageBaseProps) => {
  // Lock buttons row when emoji picker is open
  const [buttonsLocked, setButtonsLocked] = useState(false)
  const [, chatStore] = useChatStore()

  const [deferredMarkUnreadActive, setDeferredMarkUnreadActive] =
    useState(isMarkUnreadActive)

  useEffect(() => {
    // skip case when unmount component
    // because broke next mount component
    if (isMarkUnreadActive === undefined) {
      return
    }

    if (isMarkUnreadActive) {
      setDeferredMarkUnreadActive(isMarkUnreadActive)
    } else {
      setTimeout(() => {
        setDeferredMarkUnreadActive(isMarkUnreadActive)
      }, 5_000)
    }
  }, [isMarkUnreadActive, setDeferredMarkUnreadActive])

  // Prepare parent element for AdditionalInfo
  const [messageElWrap, setMessageElWrap] = useState<Element | undefined>()
  const messageImageCntRef = useRef<HTMLElement | undefined>(null)
  const [messageElImageBtn, setMessageElImageBtn] = useState<
    HTMLElement | undefined
  >()
  const [active, setActive] = useState(false)

  const elements = useRef<HTMLElement[]>([])

  useLayoutEffect(() => {
    if (!messageElWrap) {
      return undefined
    }

    messageElWrap.innerHTML = html
    const toDispose: {el: HTMLElement; listener: (e: MouseEvent) => void}[] = []
    let lastImageEl: HTMLImageElement = undefined
    let onImageLoadListener: () => void = undefined

    messageElWrap
      ?.querySelectorAll<HTMLElement>('.message-image-cnt')
      ?.forEach(imageCntEl => {
        const el = imageCntEl.querySelector<HTMLElement>('.message-image-btn')
        if (!el) {
          return
        }

        elements.current.push(el)

        if (lastImage) {
          lastImageEl = imageCntEl.querySelector<HTMLImageElement>('img')
          if (lastImageEl) {
            onImageLoadListener = () => chatStore.scrollToFirstUnread()
            lastImageEl.addEventListener('load', onImageLoadListener)
          } else {
            logger.warn('Cannot find image for scroll bottom')
          }
        }

        const listener = (e: MouseEvent) => {
          setMessageElImageBtn(e.currentTarget as HTMLElement)
          setActive(true)

          const elBg = imageCntEl.querySelector('.message-image-bg')
          if (elBg) {
            elBg.classList.remove('bg-secondary')
            elBg.classList.add('bg-action-default')
          }

          messageImageCntRef.current = imageCntEl
          imageCntEl.classList.add('message-image-lock')
        }

        el.addEventListener('click', listener)
        toDispose.push({el, listener})
        el.innerHTML = ReactDOMServer.renderToString(<ImageMenuIcon />)
      })

    return () => {
      for (const {el, listener} of toDispose) {
        el.removeEventListener('click', listener)
      }
      if (lastImageEl && onImageLoadListener) {
        lastImageEl.removeEventListener('load', onImageLoadListener)
      }
    }
  }, [html, messageElWrap, voting])

  const hasReactions = !!reactions?.length

  const onActiveOff = () => {
    messageImageCntRef.current?.classList.remove('message-image-lock')
    setActive(false)

    const elBg =
      messageImageCntRef.current?.querySelector<HTMLElement>(
        '.message-image-bg'
      )
    if (elBg) {
      elBg.classList.remove('bg-interactive')
      elBg.classList.add('bg-secondary')
    }
  }

  const featureTurnOff = false

  return (
    <TwChatMessageContainer
      $my={my}
      $fullView={fullView}
      $hideAvatar={!user || hideAvatar}
      $animateShow={animateShow}
      $editing={editing}
      className={className}
      id={`chat-msg-${timestamp}`}
      data-message-timestamp={timestamp}
    >
      <ImageMenuDropdown
        reference={messageElImageBtn}
        url={messageElImageBtn?.dataset?.url ?? ''}
        onSetBackground={chatStore.isTask && onSetBackground}
        onDownloadFile={onDownloadFile}
        onRemoveBackground={chatStore.isTask && onRemoveBackground}
        active={active}
        setActive={onActiveOff}
      />
      {user && !my && fullView && !hideAvatar && (
        <AvatarBase
          ref={avatarRef}
          user={user}
          size={32}
          className="flex-none"
          onClick={onAvatarClick}
          hideOutline
          hideStatus
        />
      )}
      <TwChatMessage $my={my}>
        <TwButtonsRow
          id={label && ChatUtils.toChatLabelId(timestamp)}
          className="mb-8"
        >
          <UserNameBase name={userName} type={userNameType} />
          {labelEditing ? (
            <PinnedLabelEditing label={label} onSubmit={onLabelSubmit} />
          ) : (
            <>
              {label && <PinnedLabel label={label} onClick={onEditPin} />}
              <TwButtons $locked={buttonsLocked}>
                {onReactionSelect && (
                  <EmojiPicker
                    onSelect={onReactionSelect}
                    closeOnSelect
                    large
                    onAfterToggle={setButtonsLocked}
                  />
                )}
                {onReply && <ReplyButton onClick={onReply} />}
                {onForward && <ForwardButton onClick={onForward} />}
                {onTogglePin && (
                  <PinButton isPinned={!!label} onClick={onTogglePin} />
                )}
                {onEdit && (
                  <EditButton timestamp={editedTimestamp} onClick={onEdit} />
                )}
                {onDelete && <DeleteButton onClick={onDelete} />}
                {featureTurnOff && canMarkUnread && onMarkUnread && (
                  <MarkUnreadButton onClick={onMarkUnread} />
                )}
                {canMarkUnread && onMarkImportant && (
                  <MarkImportantButton
                    onClick={onMarkImportant}
                    active={deferredMarkUnreadActive}
                  />
                )}
              </TwButtons>

              {canMarkUnread && deferredMarkUnreadActive && onMarkImportant && (
                <div className="absolute right-0">
                  <MarkImportantButton
                    onClick={onMarkImportant}
                    active={deferredMarkUnreadActive}
                  />
                </div>
              )}
            </>
          )}
        </TwButtonsRow>
        {replyMessageData && (
          <CitedMessage
            data={replyMessageData}
            onClick={onRepliedMessageClick}
            onFileClick={onFileClick}
            heightRestricted
            hoverable
          />
        )}
        {forwardedMessageData && (
          <>
            <div className="text-12 leading-4 text-secondary mb-8">
              {Lang.msg.messageForwarding.forwardedMessage}
            </div>
            <CitedMessage
              data={forwardedMessageData}
              onClick={onForwardedMessageClick}
              onFileClick={onFileClick}
              hoverable
            />
          </>
        )}
        <div
          className={cn(
            'flex flex-wrap',
            hasReactions ? 'flex-col gap-16' : 'gap-4'
          )}
        >
          {!forwardedMessageData && html && (
            <Message
              ref={setMessageElWrap}
              emojiOnly={emojiOnly}
              onClick={onMessageClick}
              className={cn(replyMessageData && 'mt-16', 'w-fit flex-grow')}
            />
          )}
          {!forwardedMessageData && !!files?.length && (
            <FileList
              files={files}
              className={cn((replyMessageData || html) && 'mt-16')}
              onClick={onFileClick}
            />
          )}

          {!voting && (
            <MessageFooter
              timestamp={timestamp}
              editedTimestamp={editedTimestamp}
              seenBy={seenBy}
              seenByTitle={seenByTitle}
              reactions={reactions}
              currentUserId={currentUserId}
              onReactionSelect={onReactionSelect}
            />
          )}
        </div>

        {voting && (
          <VotingComp
            voting={voting}
            hubId={hubId}
            msgTs={msgTs}
            timestamp={timestamp}
            editedTimestamp={editedTimestamp}
            seenBy={seenBy}
            seenByTitle={seenByTitle}
            reactions={reactions}
            currentUserId={currentUserId}
            onReactionSelect={onReactionSelect}
          />
        )}

        <TwUiCnt />
        {fullView && <TwTail my={my} />}
        <TwChatMessageBottom />
      </TwChatMessage>
    </TwChatMessageContainer>
  )
}

interface MessageFooterProps {
  timestamp: number
  editedTimestamp: number | undefined
  seenBy: SeenBy | undefined
  seenByTitle: string | undefined
  reactions?: ChatMessageReaction[]
  currentUserId?: DataObjectId
  onReactionSelect?: (reaction: string) => void
}

export const MessageFooter = (props: MessageFooterProps) => {
  const {reactions, currentUserId, onReactionSelect} = props
  const {timestamp, editedTimestamp, seenBy, seenByTitle} = props
  const hasReactions = reactions?.length

  return (
    <div className="flex items-end flex-grow w-full flex-wrap gap-4">
      {hasReactions ? (
        <ReactionsList
          currentUserId={currentUserId}
          reactions={reactions}
          onSelect={onReactionSelect}
        />
      ) : null}

      <div className={'ml-auto flex'}>
        <AdditionalInfo
          timestamp={timestamp}
          editedTimestamp={editedTimestamp}
          seenBy={seenBy}
          seenByTitle={seenByTitle}
        />
      </div>
    </div>
  )
}
